import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Box,
    Typography,
    IconButton,
    Divider,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import moment from 'moment-timezone'
import {
    InvoiceCreateRequest,
    InvoiceType,
    PaymentTerms,
    InvoiceLineItem,
    clientInvoicesApi,
} from '@assembly/api/clientInvoices'

interface Props {
    open: boolean
    onClose: () => void
    onSuccess: () => void
}

interface FormData {
    organizationId: string
    assemblyLineId: string
    type: InvoiceType
    paymentTerms: PaymentTerms
    issueDate: moment.Moment | null
    billingPeriodStart: moment.Moment | null
    billingPeriodEnd: moment.Moment | null
    description: string
    notes: string
    lineItems: InvoiceLineItem[]
    taxAmount?: number
}

const defaultLineItem: InvoiceLineItem = {
    description: '',
    quantity: 1,
    unit: 'hours',
    unitPrice: 0,
    totalAmount: 0,
    type: 'CONTRACTOR_HOURS',
}

export default function CreateInvoiceDialog({ open, onClose, onSuccess }: Props) {
    const { addAlert } = useSnackbars()
    const [loading, setLoading] = useState(false)
    const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([{ ...defaultLineItem }])

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isValid },
    } = useForm<FormData>({
        defaultValues: {
            organizationId: '',
            assemblyLineId: '',
            type: 'MONTHLY',
            paymentTerms: 'NET_30',
            issueDate: moment(),
            billingPeriodStart: moment(),
            billingPeriodEnd: moment(),
            description: '',
            notes: '',
            lineItems: [],
            taxAmount: undefined,
        },
        mode: 'onChange',
    })



    useEffect(() => {
        if (open) {
            reset()
            setLineItems([{ ...defaultLineItem }])
        }
    }, [open, reset])

    const handleAddLineItem = () => {
        setLineItems([...lineItems, { ...defaultLineItem }])
    }

    const handleRemoveLineItem = (index: number) => {
        if (lineItems.length > 1) {
            const newLineItems = lineItems.filter((_, i) => i !== index)
            setLineItems(newLineItems)
            setValue('lineItems', newLineItems)
        }
    }

    const handleLineItemChange = (index: number, field: keyof InvoiceLineItem, value: any) => {
        const newLineItems = [...lineItems]
        newLineItems[index] = { ...newLineItems[index], [field]: value }

        // If changing type to SUBSCRIPTION, set quantity to 1 and unit to MONTHLY
        if (field === 'type' && value === 'SUBSCRIPTION') {
            newLineItems[index].quantity = 1
            newLineItems[index].unit = 'MONTHLY'
        }

        // Calculate total amount
        if (field === 'quantity' || field === 'unitPrice' || field === 'type') {
            const item = newLineItems[index]
            if (item.type === 'SUBSCRIPTION') {
                // For subscriptions, total equals unit price (quantity is always 1)
                newLineItems[index].totalAmount = item.unitPrice || 0
            } else {
                // For other types, calculate normally
                const quantity = field === 'quantity' ? value : item.quantity
                const unitPrice = field === 'unitPrice' ? value : item.unitPrice
                newLineItems[index].totalAmount = quantity * unitPrice
            }
        }

        setLineItems(newLineItems)
        setValue('lineItems', newLineItems)
    }

    const calculateSubtotal = () => {
        return lineItems.reduce((sum, item) => sum + (item.totalAmount || 0), 0)
    }

    const calculateTax = () => {
        const taxAmount = watch('taxAmount')
        if (taxAmount !== undefined && taxAmount !== null) {
            return taxAmount
        }
        const subtotal = calculateSubtotal()
        return subtotal * 0.08 // 8% tax rate default
    }

    const calculateTotal = () => {
        return calculateSubtotal() + calculateTax()
    }

    const onSubmit = async (data: FormData) => {
        try {
            setLoading(true)

            const invoiceData: InvoiceCreateRequest = {
                ...data,
                issueDate: data.issueDate ? data.issueDate.valueOf() : Date.now(),
                billingPeriodStart: data.billingPeriodStart ? data.billingPeriodStart.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                billingPeriodEnd: data.billingPeriodEnd ? data.billingPeriodEnd.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                lineItems: lineItems.filter(item => item.description && item.quantity > 0),
                taxAmount: data.taxAmount !== undefined && data.taxAmount !== null ? data.taxAmount : undefined,
            }

            await clientInvoicesApi.createInvoice(invoiceData)
            addAlert({ message: 'Invoice created successfully', type: SnackbarType.Success })
            onSuccess()
        } catch (error) {
            addAlert({ message: 'Failed to create invoice', type: SnackbarType.Error })
            console.error('Error creating invoice:', error)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Create New Invoice</DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent>
                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Basic Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="organizationId"
                                control={control}
                                rules={{ required: 'Organization is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Organization ID"
                                        error={!!errors.organizationId}
                                        helperText={errors.organizationId?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="assemblyLineId"
                                control={control}
                                rules={{ required: 'Assembly Line is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Assembly Line ID"
                                        error={!!errors.assemblyLineId}
                                        helperText={errors.assemblyLineId?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="type"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel>Invoice Type</InputLabel>
                                        <Select {...field} label="Invoice Type">
                                            <MenuItem value="ONE_TIME">One Time</MenuItem>
                                            <MenuItem value="MONTHLY">Monthly</MenuItem>
                                            <MenuItem value="PRORATED">Prorated</MenuItem>
                                            <MenuItem value="CLOSING">Closing</MenuItem>
                                            <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                                            <MenuItem value="INTERNAL">Internal</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Controller
                                name="paymentTerms"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel>Payment Terms</InputLabel>
                                        <Select {...field} label="Payment Terms">
                                            <MenuItem value="NET_0">Net 0</MenuItem>
                                            <MenuItem value="NET_7">Net 7</MenuItem>
                                            <MenuItem value="NET_15">Net 15</MenuItem>
                                            <MenuItem value="NET_30">Net 30</MenuItem>
                                            <MenuItem value="NET_60">Net 60</MenuItem>
                                            <MenuItem value="NET_90">Net 90</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Controller
                                    name="issueDate"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            label="Issue Date"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: !!errors.issueDate,
                                                    helperText: errors.issueDate?.message,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Controller
                                    name="billingPeriodStart"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            label="Billing Period Start"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: !!errors.billingPeriodStart,
                                                    helperText: errors.billingPeriodStart?.message,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <Controller
                                    name="billingPeriodEnd"
                                    control={control}
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            label="Billing Period End"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    error: !!errors.billingPeriodEnd,
                                                    helperText: errors.billingPeriodEnd?.message,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Invoice Description"
                                        multiline
                                        rows={6}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="notes"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Internal Notes"
                                        multiline
                                        rows={3}
                                    />
                                )}
                            />
                        </Grid>

                        {/* Line Items */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">Line Items</Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={handleAddLineItem}
                                    variant="outlined"
                                    size="small"
                                >
                                    Add Line Item
                                </Button>
                            </Box>
                        </Grid>

                        {lineItems.map((item, index) => {
                            const isSubscription = item.type === 'SUBSCRIPTION'
                            return (
                                <Grid item xs={12} key={index}>
                                    <Box border={1} borderColor="divider" borderRadius={1} p={2}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={2.5}>
                                                <TextField
                                                    fullWidth
                                                    label="Description"
                                                    value={item.description}
                                                    onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Type</InputLabel>
                                                    <Select
                                                        value={item.type || 'CONTRACTOR_HOURS'}
                                                        label="Type"
                                                        onChange={(e) => handleLineItemChange(index, 'type', e.target.value)}
                                                    >
                                                        <MenuItem value="SUBSCRIPTION">Subscription</MenuItem>
                                                        <MenuItem value="CONTRACTOR_HOURS">Hours</MenuItem>
                                                        <MenuItem value="SETUP_FEE">Setup Fee</MenuItem>
                                                        <MenuItem value="OVERAGE">Overage</MenuItem>
                                                        <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
                                                        <MenuItem value="DISCOUNT">Discount</MenuItem>
                                                        <MenuItem value="TAX">Tax</MenuItem>
                                                        <MenuItem value="OTHER">Other</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            {!isSubscription && (
                                                <>
                                                    <Grid item xs={6} md={1.5}>
                                                        <TextField
                                                            fullWidth
                                                            label="Quantity"
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6} md={2}>
                                                        <TextField
                                                            fullWidth
                                                            label="Unit"
                                                            value={item.unit}
                                                            onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                                                            size="small"
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                            {isSubscription && (
                                                <Grid item xs={6} md={3.5}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Period</InputLabel>
                                                        <Select
                                                            value={item.unit || 'MONTHLY'}
                                                            label="Period"
                                                            onChange={(e) => handleLineItemChange(index, 'unit', e.target.value)}
                                                        >
                                                            <MenuItem value="MONTHLY">Monthly</MenuItem>
                                                            <MenuItem value="ANNUAL">Annual</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            )}
                                            <Grid item xs={6} md={2}>
                                                <TextField
                                                    fullWidth
                                                    label="Unit Price"
                                                    type="number"
                                                    value={item.unitPrice}
                                                    onChange={(e) => handleLineItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                                    size="small"
                                                    InputProps={{
                                                        startAdornment: <Typography variant="caption">$</Typography>,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2}>
                                                <TextField
                                                    fullWidth
                                                    label="Total Amount"
                                                    value={`$${item.totalAmount?.toFixed(2) || '0.00'}`}
                                                    size="small"
                                                    InputProps={{
                                                        readOnly: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={1}>
                                                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                                    <IconButton
                                                        onClick={() => handleRemoveLineItem(index)}
                                                        disabled={lineItems.length === 1}
                                                        color="error"
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            )
                        })}

                        {/* Totals */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Box display="flex" justifyContent="flex-end">
                                <Box width="300px">
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography>Subtotal:</Typography>
                                        <Typography>${calculateSubtotal().toFixed(2)}</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" mb={1} alignItems="flex-start">
                                        <Box display="flex" flexDirection="column">
                                            <Typography>Tax:</Typography>
                                            {watch('taxAmount') === undefined && (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                    (Auto: 8%)
                                                </Typography>
                                            )}
                                        </Box>
                                        <Controller
                                            name="taxAmount"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    size="small"
                                                    type="number"
                                                    value={field.value ?? ''}
                                                    onChange={(e) => {
                                                        const value = e.target.value === '' ? undefined : parseFloat(e.target.value) || 0
                                                        field.onChange(value)
                                                    }}
                                                    InputProps={{
                                                        startAdornment: <Typography variant="caption" sx={{ mr: 0.5 }}>$</Typography>,
                                                    }}
                                                    sx={{ width: '120px' }}
                                                    placeholder="Auto"
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography variant="h6">Total:</Typography>
                                        <Typography variant="h6">${calculateTotal().toFixed(2)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !isValid}
                    >
                        {loading ? 'Creating...' : 'Create Invoice'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
